import type React from 'react';
import type { ContentTypeRegistration } from './types';
import type { ContentTypeDefinition } from '../schema';

/**
 * ContentRegistry - Registers and manages content type packages
 */
export class ContentRegistry {
    private static instance: ContentRegistry;
    private registrations: Map<string, ContentTypeRegistration> = new Map();
    private listeners: Set<(type: string, registration: ContentTypeRegistration) => void> =
        new Set();

    private constructor() {}

    /**
     * Get singleton instance
     */
    static getInstance(): ContentRegistry {
        if (!ContentRegistry.instance) {
            ContentRegistry.instance = new ContentRegistry();
        }
        return ContentRegistry.instance;
    }

    /**
     * Reset singleton (mainly for testing)
     */
    static resetInstance(): void {
        if (ContentRegistry.instance) {
            ContentRegistry.instance.clear();
        }
        ContentRegistry.instance = new ContentRegistry();
    }

    /**
     * Register a content type from definition
     */
    register<TData = unknown, TAnswer = unknown>(
        definition: ContentTypeDefinition<TData, TAnswer>,
        options?: {
            iframeSrc?: string;
            cdnUrl?: string;
        }
    ): void {
        const registration: ContentTypeRegistration<TData, TAnswer> = {
            type: definition.schema.type,
            title: definition.schema.title,
            description: definition.schema.description,
            version: definition.schema.version,
            icon: definition.schema.icon,
            category: definition.schema.category,
            definition,
            iframeSrc: options?.iframeSrc,
            cdnUrl: options?.cdnUrl,
        };

        this.registerType(registration);
    }

    /**
     * Register a content type directly
     */
    registerType<TData = unknown, TAnswer = unknown>(
        registration: ContentTypeRegistration<TData, TAnswer>
    ): void {
        if (this.registrations.has(registration.type)) {
            console.warn(`Content type "${registration.type}" is already registered. Overwriting.`);
        }
        this.registrations.set(registration.type, registration as ContentTypeRegistration);

        // Notify listeners
        this.listeners.forEach((listener) =>
            listener(registration.type, registration as ContentTypeRegistration)
        );
    }

    /**
     * Unregister a content type
     */
    unregister(type: string): boolean {
        return this.registrations.delete(type);
    }

    /**
     * Get a content type registration
     */
    get<TData = unknown, TAnswer = unknown>(
        type: string
    ): ContentTypeRegistration<TData, TAnswer> | undefined {
        return this.registrations.get(type) as ContentTypeRegistration<TData, TAnswer> | undefined;
    }

    /**
     * Get content type definition
     */
    getDefinition<TData = unknown, TAnswer = unknown>(
        type: string
    ): ContentTypeDefinition<TData, TAnswer> | undefined {
        const registration = this.get<TData, TAnswer>(type);
        return registration?.definition;
    }

    /**
     * Check if a content type is registered
     */
    has(type: string): boolean {
        return this.registrations.has(type);
    }

    /**
     * Get all registered types
     */
    getAll(): ContentTypeRegistration[] {
        return Array.from(this.registrations.values());
    }

    /**
     * Get all type names
     */
    getTypes(): string[] {
        return Array.from(this.registrations.keys());
    }

    /**
     * Get types by category
     */
    getByCategory(category: string): ContentTypeRegistration[] {
        return this.getAll().filter((reg) => reg.category === category);
    }

    /**
     * Get iframe source for a content type
     */
    getIframeSrc(type: string): string | undefined {
        return this.registrations.get(type)?.iframeSrc;
    }

    /**
     * Get CDN URL for a content type
     */
    getCdnUrl(type: string): string | undefined {
        return this.registrations.get(type)?.cdnUrl;
    }

    /**
     * Get Player component for a content type
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getPlayerComponent(type: string): React.ComponentType<any> | undefined {
        const registration = this.registrations.get(type);
        return registration?.definition.PlayerComponent as React.ComponentType<any> | undefined;
    }

    /**
     * Get Edit component for a content type
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getEditComponent(type: string): React.ComponentType<any> | undefined {
        const registration = this.registrations.get(type);
        return registration?.definition.EditComponent as React.ComponentType<any> | undefined;
    }

    /**
     * Get grader for a content type
     */
    getGrader(type: string) {
        const registration = this.registrations.get(type);
        return registration?.definition.grader;
    }

    /**
     * Get validator for a content type
     */
    getValidator(type: string) {
        const registration = this.registrations.get(type);
        return registration?.definition.validator;
    }

    /**
     * Subscribe to registration changes
     */
    onRegister(
        listener: (type: string, registration: ContentTypeRegistration) => void
    ): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /**
     * Clear all registrations
     */
    clear(): void {
        this.registrations.clear();
        this.listeners.clear();
    }

    /**
     * Get registration count
     */
    get size(): number {
        return this.registrations.size;
    }
}

/**
 * Get the global content registry
 */
export function getRegistry(): ContentRegistry {
    return ContentRegistry.getInstance();
}

/**
 * Register a content type
 */
export function registerContentType<TData = unknown, TAnswer = unknown>(
    definition: ContentTypeDefinition<TData, TAnswer>,
    options?: { iframeSrc?: string; cdnUrl?: string }
): void {
    ContentRegistry.getInstance().register(definition, options);
}

/**
 * Check if content type is registered
 */
export function isContentTypeRegistered(type: string): boolean {
    return ContentRegistry.getInstance().has(type);
}
